import React, { createContext, useContext, useEffect, useState } from 'react'
import { DEFAULT_GENERATION_SCOPE, cancelScopeRuns, forgetScopeRuns, hasScopeRuns } from './generation-runs'
import { forgetWiredShaderInputs } from './wired-inputs'

const ScopeContext = createContext<string>(DEFAULT_GENERATION_SCOPE)

/** The editor a Shader node belongs to; runs and wired inputs are kept per editor. */
export function useShaderGenerationScope(): string {
  return useContext(ScopeContext)
}

interface ShaderGenerationScopeProps {
  /** Every node the editor holds, on screen or not. */
  nodes: readonly { id: string }[]
  children: React.ReactNode
}

/**
 * Owns the shader generations started inside one editor. A run whose node is
 * removed, or whose editor unmounts, is cancelled: nothing would receive its
 * result, and a paid repair round must not run for a node that is gone.
 * Scrolling a node off-screen only unmounts the node, so its run carries on.
 */
export function ShaderGenerationScope({ nodes, children }: ShaderGenerationScopeProps) {
  const [scope] = useState(() => `editor-${crypto.randomUUID()}`)

  useEffect(() => {
    if (!hasScopeRuns(scope)) return
    const ids = new Set(nodes.map((node) => node.id))
    cancelScopeRuns(scope, (nodeId) => ids.has(nodeId))
  }, [scope, nodes])

  useEffect(() => () => {
    cancelScopeRuns(scope)
    forgetScopeRuns(scope)
    forgetWiredShaderInputs(scope)
  }, [scope])

  return <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>
}
